"use client"

import type React from "react"

import { useState, useEffect, useCallback } from "react"
import { useAuthStore } from "@/lib/auth-store"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { ThumbsUp, ThumbsDown, Plus, Loader2 } from "@/lib/icons"
import { toast } from "sonner"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import type { TagCategory } from "@/lib/tag-taxonomy-store"
import { getTagColor, getTagTextColor } from "@/lib/tag-categorizer"
import type { TagProposal } from "@/lib/tag-proposals"
import { useTranslations } from "next-intl"

interface SuggestedTagsPanelProps {
  contentId: string
  onTagAdd?: (tag: string) => void
  availableTags?: string[]
  tagType?: "general" | "series"
}

type ExtendedTagCategory = TagCategory | "sound_designer"

export function SuggestedTagsPanel({
  contentId,
  onTagAdd,
  availableTags = [],
  tagType = "general",
}: SuggestedTagsPanelProps) {
  const tt = useTranslations("suggestedTagsPanel")
  const { user } = useAuthStore()
  const [proposals, setProposals] = useState<TagProposal[]>([])
  const [approveThreshold, setApproveThreshold] = useState(6)
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [votingId, setVotingId] = useState<string | null>(null)
  const [newTag, setNewTag] = useState("")
  const [selectedCategory, setSelectedCategory] = useState<ExtendedTagCategory>(
    tagType === "series" ? "copyright" : "general",
  )
  const [showAutocomplete, setShowAutocomplete] = useState(false)
  const [filteredTags, setFilteredTags] = useState<string[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)

  const site = contentId.split(":")[0]

  const refresh = useCallback(() => {
    fetch(`/api/tag-proposals?contentId=${encodeURIComponent(contentId)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!data) return
        setProposals(data.proposals ?? [])
        if (data.thresholds?.approve) setApproveThreshold(data.thresholds.approve)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [contentId])

  useEffect(() => {
    setLoading(true)
    refresh()
  }, [refresh])

  // Autocomplete: the post's own tags first (instant), then the post's own
  // booru's real tag list once that's back — see app/api/booru/tags/route.ts.
  useEffect(() => {
    const term = newTag.trim()
    if (term.length < 2) {
      setShowAutocomplete(false)
      setFilteredTags([])
      return
    }
    const searchTerm = term.toLowerCase().replace(/\s+/g, "_")
    const local = availableTags.filter((tag) => tag.toLowerCase().includes(searchTerm)).slice(0, 10)
    setFilteredTags(local)
    setShowAutocomplete(local.length > 0)
    setSelectedIndex(0)

    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`/api/booru/tags?q=${encodeURIComponent(searchTerm)}&site=${encodeURIComponent(site)}&limit=10`)
        if (!res.ok || cancelled) return
        const data = await res.json()
        const remote: string[] = Array.isArray(data.suggestions) ? data.suggestions : []
        if (cancelled || remote.length === 0) return
        const merged = Array.from(new Set([...remote, ...local])).slice(0, 10)
        setFilteredTags(merged)
        setShowAutocomplete(merged.length > 0)
      } catch {
        // Keep the local matches; autocomplete is a convenience, not required.
      }
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [newTag, availableTags, site])

  const formatTagWithCategory = (tag: string, category: ExtendedTagCategory): string => {
    if (category === "general") return tag
    if (category === "artist") return `artist:${tag}`
    if (category === "va") return `voiced_by_${tag}`
    if (category === "sound_designer") return `sound_design_by_${tag}`
    if (category === "character") return `character:${tag}`
    if (category === "copyright") return `copyright:${tag}`
    return tag
  }

  const selectTagFromAutocomplete = (tag: string) => {
    setNewTag(tag)
    setShowAutocomplete(false)
    setFilteredTags([])
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showAutocomplete) {
      if (e.key === "Enter") handleSuggestTag()
      return
    }
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault()
        setSelectedIndex((prev) => (prev < filteredTags.length - 1 ? prev + 1 : prev))
        break
      case "ArrowUp":
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
        break
      case "Enter":
        e.preventDefault()
        if (filteredTags[selectedIndex]) selectTagFromAutocomplete(filteredTags[selectedIndex])
        else handleSuggestTag()
        break
      case "Escape":
        setShowAutocomplete(false)
        break
    }
  }

  const handleSuggestTag = async () => {
    if (!newTag.trim()) return
    if (!user) {
      toast.error(tt("signInToSuggestTags"))
      return
    }
    const formattedTag = formatTagWithCategory(newTag.trim(), selectedCategory)
    setSubmitting(true)
    try {
      const res = await fetch("/api/tag-proposals", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contentId, tag: formattedTag, kind: "add" }),
      })
      const data = await res.json()
      if (!res.ok && res.status !== 409) throw new Error(data.error || "Failed")
      setNewTag("")
      setShowAutocomplete(false)
      setSelectedCategory("general")
      if (res.status === 409) {
        toast.message(data.error)
      } else {
        toast.success(tt("tagSuggestionAddedNeedsCommunityApproval"))
        onTagAdd?.(formattedTag)
      }
      refresh()
    } catch (err: any) {
      toast.error(err.message || tt("somethingWentWrong"))
    } finally {
      setSubmitting(false)
    }
  }

  const castVote = async (proposal: TagProposal, vote: 1 | -1 | 0) => {
    if (!user) {
      toast.error(tt("signInToVote"))
      return
    }
    setVotingId(proposal.id)
    // Optimistic: flip the vote locally so the button feels instant, then
    // reconcile with whatever the server actually recorded.
    setProposals((prev) => prev.map((p) => (p.id !== proposal.id ? p : { ...p, myVote: vote })))
    try {
      const res = await fetch(`/api/tag-proposals/${proposal.id}/vote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vote }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || "Failed to vote")
      if (data.proposal) setProposals((prev) => prev.map((p) => (p.id === proposal.id ? data.proposal : p)))
    } catch (err: any) {
      toast.error(err.message || tt("somethingWentWrong"))
      refresh()
    } finally {
      setVotingId(null)
    }
  }

  const handleUpvote = (proposal: TagProposal) => castVote(proposal, proposal.myVote === 1 ? 0 : 1)
  const handleDownvote = (proposal: TagProposal) => castVote(proposal, proposal.myVote === -1 ? 0 : -1)

  const pendingTags = proposals.filter((p) => p.status === "pending" && p.kind === "add")

  const VoteButton = ({
    isActive,
    onClick,
    icon: Icon,
    disabled,
  }: {
    isActive: boolean
    onClick: () => void
    icon: typeof ThumbsUp
    disabled?: boolean
  }) => (
    <Button
      size="sm"
      variant={isActive ? "default" : "ghost"}
      className={`h-6 w-6 p-0 ${isActive ? "bg-primary text-primary-foreground" : ""}`}
      disabled={disabled}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onClick()
      }}
    >
      <Icon className={`h-3 w-3 ${isActive ? "fill-current" : ""}`} />
    </Button>
  )

  return (
    <div className="space-y-4 px-2">
      <div className="space-y-2">
        <label className="font-medium mb-0 text-sm pb-0">
          {tagType === "series" ? tt("suggestASeries") : tt("suggestATag")}
        </label>
        <div className="flex gap-2 mt-1.5">
          <div className="relative flex-1">
            <Input
              placeholder={tagType === "series" ? tt("enterSeriesName") : tt("enterTagName")}
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={() => setTimeout(() => setShowAutocomplete(false), 200)}
              onFocus={() => {
                if (filteredTags.length > 0) setShowAutocomplete(true)
              }}
              className="h-8 mt-0"
              disabled={submitting}
            />
            {showAutocomplete && filteredTags.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-popover border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                {filteredTags.map((tag, index) => (
                  <button
                    key={tag}
                    type="button"
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-accent/50 transition-colors ${
                      index === selectedIndex ? "bg-accent" : ""
                    }`}
                    onClick={() => selectTagFromAutocomplete(tag)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <span className="font-medium">{tag}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Select value={selectedCategory} onValueChange={(value) => setSelectedCategory(value as ExtendedTagCategory)}>
            <SelectTrigger className="h-8 w-[156px] mt-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {tagType === "series" ? (
                <>
                  <SelectItem value="copyright">{tt("series")}</SelectItem>
                  <SelectItem value="character">{tt("character")}</SelectItem>
                </>
              ) : (
                <>
                  <SelectItem value="general">{tt("general")}</SelectItem>
                  <SelectItem value="artist">{tt("artist")}</SelectItem>
                  <SelectItem value="va">{tt("voiceActor")}</SelectItem>
                  <SelectItem value="sound_designer">{tt("soundDesigner")}</SelectItem>
                  <SelectItem value="character">{tt("character")}</SelectItem>
                  <SelectItem value="copyright">{tt("seriesCopyright")}</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
          <Button className="pt-0 mt-0.5" onClick={handleSuggestTag} size="sm" disabled={submitting}>
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
        {!user && (
          <p className="text-xs text-muted-foreground">{tt("signInToSuggestTags")}</p>
        )}
        {selectedCategory !== "general" && (
          <div className="mt-2 flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{tt("preview")}</span>
            {newTag && (
              <Badge
                className={`${getTagColor(selectedCategory === "sound_designer" ? "artist" : selectedCategory)} ${getTagTextColor(selectedCategory === "sound_designer" ? "artist" : selectedCategory)}`}
              >
                {formatTagWithCategory(newTag, selectedCategory)}
              </Badge>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      ) : pendingTags.length > 0 ? (
        <div className="space-y-2">
          <h4 className="text-sm font-semibold">{tt("pendingSuggestions")}</h4>
          <ScrollArea className="h-48">
            <div className="space-y-2 pr-0">
              {pendingTags.map((proposal) => (
                <div key={proposal.id} className="flex items-center justify-between p-2 rounded-md bg-secondary/30">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{proposal.tag}</Badge>
                    <div className="flex gap-3 text-xs text-muted-foreground">
                      <span className="text-green-600">{tt("upvotesValueApproveAt", { upvotes: proposal.upvotes, approveThreshold })}</span>
                      <span className="text-red-600">{tt("downvotesValue", { downvotes: proposal.downvotes })}</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <VoteButton isActive={proposal.myVote === 1} onClick={() => handleUpvote(proposal)} icon={ThumbsUp} disabled={votingId === proposal.id} />
                    <VoteButton isActive={proposal.myVote === -1} onClick={() => handleDownvote(proposal)} icon={ThumbsDown} disabled={votingId === proposal.id} />
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      ) : (
        <div className="text-center py-4 text-foreground/55 text-sm">
          {tt("noSuggestedTagsYetBeTheFirstToSuggestO")}
        </div>
      )}
    </div>
  )
}
